import { Component, ChangeDetectionStrategy, ChangeDetectorRef, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { TamDashboardTileBaseComponent } from '../tam-dashboard-tile.base.component';
import { Subscription, Subject } from 'rxjs';
import { TamDashboardThreadBaseConfiguration } from '../tam-dashboard-tile.base.config';
import { ThreadDetailConfig, ThreadDetailPartialDisplay } from '../../thread-detail/thread-detail.view-model';
import { TamDashboardActions, TamDashboardEvents } from '../tam-dashboard.model';
import { filter } from 'rxjs/operators';
import { ThreadDisplaySettings } from '../../../tamalelibs/models/thread-display-settings.model';
import { NoteDialogService } from '../../note-dialog/note-dialog.service';
import { NoteDialogOpenOptions, NoteDialogType } from '../../note-dialog/note-dialog.model';

@Component({
    selector: 'tam-dashboard-thread-tile',
    templateUrl: './tam-dashboard-thread-tile.component.html',
    styleUrls: ['./tam-dashboard-thread-tile.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TamDashboardThreadTileComponent implements OnInit, AfterViewInit, OnDestroy, TamDashboardTileBaseComponent {
    id = '-1';
    name = '';
    displaySettings: object = {};
    threadConfigs: ThreadDetailConfig[] = [];
    loaded = false;
    noData = false;
    private _config: TamDashboardThreadBaseConfiguration = null;
    private _destroySubscriptions: Array<Subscription> = [];
    private _afterViewFeedback$ = new Subject<string>();

    get isPrintingMode(): boolean {
        return this._config ? this._config.isPrintingMode : false;
    }

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _noteDialogService: NoteDialogService
    ) {
    }

    ngOnInit() {
    }

    ngAfterViewInit(): void {
        this._afterViewFeedback$.next(TamDashboardEvents.TILE_AFTER_VIEW_INIT);
    }

    ngOnDestroy(): void {
        this._destroySubscriptions.forEach(subscription => {
            if (subscription && !subscription.closed) {
                subscription.unsubscribe();
            }
        });
        if (this._config) {
            this._config.destroy();
        }
    }

    setConfig(config: TamDashboardThreadBaseConfiguration): boolean {
        // modify reason, same as pie chart
        this._config = config;
        this.id = this._config.id;
        this.threadConfigs = Array.from(config.threadDetailConfigs.values());
        this._init();
        this._changeDetectorRef.detectChanges();
        return true;
    }

    createNewNote() {
        const _noteDialogOpenOptions = new NoteDialogOpenOptions();
        _noteDialogOpenOptions.openType = NoteDialogType.CreateNote;
        this._noteDialogService.dialogOpen$.next(_noteDialogOpenOptions);
    }

    private _init() {
        this._destroySubscriptions.push(
            this._config.actionSubject$.pipe(
                filter((action: any) => action && action.id === this.id),
            ).subscribe(action => this._onSubscribeActions(action)),
            this._afterViewFeedback$.subscribe((payload) => {
                if (payload === TamDashboardEvents.TILE_AFTER_VIEW_INIT) {
                    this._config.feedbackSubject$.next({
                        type: TamDashboardEvents.TILE_AFTER_VIEW_INIT,
                        id: this._config.id
                    });
                }
            })
        );
    }
    private _onSubscribeActions(action) {
        if (action.type === TamDashboardActions.SET_TILE_DATA) {
            if (action.threadConfigs) {
                this.threadConfigs = Array.from(action.threadConfigs.values());
                this.noData = false;
            } else {
                this.threadConfigs = [];
            }
            this.noData = this.threadConfigs.length === 0;
            if (action.payload) {
                this._generateDisplaySettings(action.payload);
            }
            if (action.hardRefresh === true) {
                this.loaded = false;
                this._changeDetectorRef.detectChanges();
                setTimeout(() => {
                    this.loaded = true;
                    this._changeDetectorRef.detectChanges();
                });
            } else {
                this.loaded = true;
                this._changeDetectorRef.detectChanges();
            }
            this._config.feedbackSubject$.next({
                type: TamDashboardEvents.TILE_LOADED_DATA,
                id: this._config.id
            });
        }
    }

    private _generateDisplaySettings(settings: ThreadDisplaySettings) {
        this.displaySettings['no-entities'] = !settings.entities;
        this.displaySettings['no-thread-attachments'] = !settings.threadAttachments;
        this.displaySettings['no-note-attachments'] = !settings.entryAttachments;
        this.displaySettings['no-subject'] = !settings.subject;
        this.displaySettings['no-source'] = !settings.source;
        this.displaySettings['no-display-date'] = !settings.displayDate;
        this.displaySettings['no-submit-date'] = !settings.submittedDate;
        this.displaySettings['no-submitter'] = !settings.submitter;
        this.displaySettings['no-note-type'] = !settings.noteType;
        this.displaySettings['no-sentiment'] = !settings.sentiment;
        this.displaySettings['no-priority'] = !settings.priority;
        this.displaySettings['no-adhoc'] = !settings.adhocTable;
        this.displaySettings['show-blurb'] = !settings.noteBody && settings.blurb;
        this.displaySettings['no-note-body'] = !settings.noteBody;
        this.displaySettings['non-printing-view'] = !this.isPrintingMode;

        this.threadConfigs.forEach(config => {
            const partialDisplay = new ThreadDetailPartialDisplay();
            partialDisplay.submittedDate = settings.submittedDate;
            partialDisplay.submitter = settings.submitter;
            config.partialDisplay = partialDisplay;
        });
    }

}
